<p-confirmDialog />

<div class="season-pricing" *transloco="let t" data-testid="season-pricing-inline">
  <!-- Header -->
  <div class="season-header">
    <div class="header-left">
      <i class="pi pi-sun header-icon"></i>
      <h3 class="header-title">{{ t('objects.wizard.seasons.title') }}</h3>
      <p-badge [value]="t('objects.wizard.seasons.badge')" severity="warn" />
    </div>
  </div>
  <p class="header-description">{{ t('objects.wizard.seasons.description') }}</p>

  <!-- Guide toggle -->
  <button
    type="button"
    class="guide-toggle"
    (click)="toggleGuide()"
    [attr.aria-expanded]="isGuideVisible()"
  >
    <i class="pi" [class.pi-question-circle]="!isGuideVisible()" [class.pi-times-circle]="isGuideVisible()"></i>
    <span>{{ t('objects.wizard.seasons.guide.toggle') }}</span>
    <i class="pi pi-chevron-down guide-chevron" [class.guide-chevron--open]="isGuideVisible()"></i>
  </button>

  <!-- Guide content -->
  @if (isGuideVisible()) {
    <div @slideDown class="guide" data-testid="season-guide">
      <div class="guide-section">
        <div class="guide-section__icon">
          <i class="pi pi-info-circle"></i>
        </div>
        <div class="guide-section__content">
          <h4 class="guide-section__title">{{ t('objects.wizard.seasons.guide.howItWorks') }}</h4>
          <p class="guide-section__text">{{ t('objects.wizard.seasons.guide.howItWorksDesc') }}</p>
        </div>
      </div>

      <div class="guide-section">
        <div class="guide-section__icon">
          <i class="pi pi-list-check"></i>
        </div>
        <div class="guide-section__content">
          <h4 class="guide-section__title">{{ t('objects.wizard.seasons.guide.howToAdd') }}</h4>
          <ol class="guide-steps">
            <li>{{ t('objects.wizard.seasons.guide.howToAddStep1') }}</li>
            <li>{{ t('objects.wizard.seasons.guide.howToAddStep2') }}</li>
            <li>{{ t('objects.wizard.seasons.guide.howToAddStep3') }}</li>
            <li>{{ t('objects.wizard.seasons.guide.howToAddStep4') }}</li>
            <li>{{ t('objects.wizard.seasons.guide.howToAddStep5') }}</li>
          </ol>
        </div>
      </div>

      <div class="guide-section">
        <div class="guide-section__icon">
          <i class="pi pi-sparkles"></i>
        </div>
        <div class="guide-section__content">
          <h4 class="guide-section__title">{{ t('objects.wizard.seasons.guide.tips') }}</h4>
          <ul class="guide-tips">
            <li>
              <i class="pi pi-palette"></i>
              <span>{{ t('objects.wizard.seasons.guide.tip2') }}</span>
            </li>
            <li>
              <i class="pi pi-shield"></i>
              <span>{{ t('objects.wizard.seasons.guide.tip3') }}</span>
            </li>
            <li>
              <i class="pi pi-pencil"></i>
              <span>{{ t('objects.wizard.seasons.guide.tip4') }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  }

  <!-- Existing Seasons List -->
  @if (seasons().length > 0) {
    <div class="seasons-list">
      @for (season of seasons(); track season.id) {
        <div @fadeSlideIn class="season-item-wrapper">
          <div class="season-item" [class.editing]="editingSeasonId() === season.id" [attr.data-testid]="'season-inline-item-' + season.id">
            <div class="season-info">
              <span class="season-dot" [style.background-color]="season.color"></span>
              <span class="season-name">{{ season.name }}</span>
              <span class="season-dates">{{
                formatDateRange(season.startDate, season.endDate)
              }}</span>
            </div>
            @if (season.minStay) {
              <span class="min-stay-tag">
                <i class="pi pi-moon"></i>
                {{ t('objects.wizard.seasons.minStayShort', { nights: season.minStay }) }}
              </span>
            }
            <div class="season-prices">
              @for (unitType of unitTypes(); track unitType.id) {
                <div class="unit-price-tag">
                  <span class="unit-name">{{ unitType.name }}</span>
                  <span class="unit-quantity">(x{{ unitType.quantity }})</span>
                  <span class="unit-price">{{ getSeasonPrice(season, unitType.id) }} PLN</span>
                  @if (seasonHasOccupancyPricing(season, unitType.id)) {
                    <span class="unit-discount">
                      {{ t('objects.wizard.seasons.occupancyPricingBadge') }}
                    </span>
                  }
                </div>
              }
            </div>
            @if (!isViewMode()) {
              <div class="season-actions">
                @if (editingSeasonId() !== season.id) {
                  <button
                    type="button"
                    class="edit-btn"
                    data-testid="season-inline-edit-btn"
                    [disabled]="isAnyFormOpen()"
                    (click)="editSeason(season)"
                    [attr.aria-label]="t('objects.wizard.seasons.editAriaLabel')"
                  >
                    <i class="pi pi-pencil"></i>
                  </button>
                }
                <button
                  type="button"
                  class="remove-btn"
                  data-testid="season-inline-remove-btn"
                  [disabled]="isAnyFormOpen() && editingSeasonId() !== season.id"
                  (click)="removeSeason(season.id)"
                  [attr.aria-label]="t('objects.wizard.seasons.removeAriaLabel')"
                >
                  <i class="pi pi-trash"></i>
                </button>
              </div>
            }
          </div>

          <!-- Inline Edit Form (under this specific season) -->
          @if (editingSeasonId() === season.id) {
            <div @slideDown class="inline-edit-form" data-testid="season-inline-edit-form">
              <ng-container *ngTemplateOutlet="seasonFormTemplate" />
            </div>
          }
        </div>
      }
    </div>
  }

  <!-- Add Season Form (at the bottom, only when adding) -->
  @if (isAddFormVisible()) {
    <div @slideDown class="add-form-container" data-testid="season-inline-add-form">
      <ng-container *ngTemplateOutlet="seasonFormTemplate" />
    </div>
  }

  <!-- Add Season Button (placeholder style, like unit-types) -->
  @if (!isViewMode() && !isAnyFormOpen()) {
    <button
      @fadeSlideIn
      type="button"
      class="add-season-placeholder"
      data-testid="season-inline-add-btn"
      (click)="showAddForm()"
    >
      <i class="pi pi-plus"></i>
      <span>{{ t('objects.wizard.seasons.addSeason') }}</span>
    </button>
  }

  <!-- Reusable Form Template -->
  <ng-template #seasonFormTemplate>
    <div class="season-form" data-testid="season-inline-form">
      <!-- Name -->
      <div class="form-field">
        <label class="field-label">{{ t('objects.wizard.seasons.seasonName') }}</label>
        <input
          pInputText
          type="text"
          data-testid="season-inline-name"
          [ngModel]="formData().name"
          (ngModelChange)="updateFormField('name', $event)"
          [placeholder]="t('objects.wizard.seasons.seasonNamePlaceholder')"
          class="w-full"
        />
      </div>

      <!-- Date Range + Min Stay -->
      <div class="form-row form-row--dates">
        <div class="form-field">
          <label class="field-label">{{ t('objects.wizard.seasons.dateFrom') }}</label>
          <p-datepicker
            data-testid="season-inline-start-date"
            [ngModel]="formData().startDate"
            (ngModelChange)="updateFormField('startDate', $event)"
            dateFormat="dd.mm.yy"
            [showIcon]="true"
            [fluid]="true"
            [placeholder]="t('objects.wizard.seasons.datePlaceholder')"
            [minDate]="today"
            [disabledDates]="disabledDates()"
            appendTo="body"
          >
            <ng-template pTemplate="date" let-date>
              @if (getSeasonColor(date); as color) {
                <span
                  class="season-date-cell"
                  [style.background-color]="color"
                  [attr.title]="getSeasonName(date)"
                >{{ date.day }}</span>
              } @else {
                {{ date.day }}
              }
            </ng-template>
          </p-datepicker>
        </div>
        <div class="form-field">
          <label class="field-label">{{ t('objects.wizard.seasons.dateTo') }}</label>
          <p-datepicker
            data-testid="season-inline-end-date"
            [ngModel]="formData().endDate"
            (ngModelChange)="updateFormField('endDate', $event)"
            dateFormat="dd.mm.yy"
            [showIcon]="true"
            [fluid]="true"
            [placeholder]="t('objects.wizard.seasons.datePlaceholder')"
            [minDate]="formData().startDate || today"
            [maxDate]="endDateMax()!"
            [disabledDates]="disabledDates()"
            appendTo="body"
          >
            <ng-template pTemplate="date" let-date>
              @if (getSeasonColor(date); as color) {
                <span
                  class="season-date-cell"
                  [style.background-color]="color"
                  [attr.title]="getSeasonName(date)"
                >{{ date.day }}</span>
              } @else {
                {{ date.day }}
              }
            </ng-template>
          </p-datepicker>
        </div>
        <div class="form-field min-stay-field">
          <label class="field-label">{{ t('objects.wizard.seasons.minStay') }}</label>
          <p-inputnumber
            data-testid="season-inline-min-stay"
            [ngModel]="formData().minStay"
            (ngModelChange)="updateFormField('minStay', $event)"
            [min]="1"
            [max]="365"
            [showButtons]="true"
            [fluid]="true"
            buttonLayout="horizontal"
            incrementButtonIcon="pi pi-plus"
            decrementButtonIcon="pi pi-minus"
            suffix=" {{ t('objects.wizard.seasons.minStaySuffix') }}"
            [placeholder]="t('objects.wizard.seasons.minStayPlaceholder')"
          />
        </div>
      </div>

      @if (otherSeasons().length > 0) {
        <div class="season-legend">
          @for (s of otherSeasons(); track s.id) {
            <div class="legend-item">
              <span class="legend-dot" [style.background-color]="s.color"></span>
              <span class="legend-label">{{ s.name }}</span>
            </div>
          }
        </div>
      }

      <!-- Unit Prices -->
      @if (unitTypes().length > 0) {
        <div class="unit-prices-section">
          <div class="section-header">
            <i class="pi pi-dollar"></i>
            <span>{{ t('objects.wizard.seasons.unitPricesTitle') }}</span>
          </div>
          <div class="unit-prices-grid">
            @for (unitType of unitTypes(); track unitType.id) {
              <div class="unit-price-input">
                <div class="unit-info">
                  <span class="unit-label">{{ unitType.name }}</span>
                  <p-badge [value]="'x' + unitType.quantity" severity="info" />
                </div>
                <div class="unit-price-fields">
                  <div class="unit-price-field">
                    <label class="field-caption">
                      {{ t('objects.wizard.seasons.pricePerNightLabel', { max: unitType.maxOccupancy }) }}
                    </label>
                    <p-inputnumber
                      [ngModel]="getFormUnitPrice(unitType.id)"
                      (ngModelChange)="setFormUnitPrice(unitType.id, $event)"
                      [min]="1"
                      [fluid]="true"
                      placeholder="0"
                    />
                  </div>
                  @if (unitType.maxOccupancy > 1 && getFormUnitPrice(unitType.id)) {
                    <div class="unit-price-field unit-price-field--occupancy">
                      <div class="occupancy-header">
                        <label class="field-caption">{{ t('objects.wizard.seasons.occupancyPricingLabel') }}</label>
                        <div class="occupancy-mode-toggle">
                          <button
                            type="button"
                            [attr.data-testid]="'season-occupancy-mode-amount-' + unitType.id"
                            [class.active]="isAmountMode(unitType.id)"
                            (click)="setOccupancyMode(unitType.id, 'amount')">
                            {{ t('objects.wizard.seasons.modeAmount') }}
                          </button>
                          <button
                            type="button"
                            [attr.data-testid]="'season-occupancy-mode-percent-' + unitType.id"
                            [class.active]="!isAmountMode(unitType.id)"
                            (click)="setOccupancyMode(unitType.id, 'percent')">
                            {{ t('objects.wizard.seasons.modePercent') }}
                          </button>
                        </div>
                      </div>
                      <div class="occupancy-tiers">
                        @for (occ of occupantsRows(unitType.id); track occ) {
                          <div class="occupancy-tier-row">
                            <span class="tier-occupants">{{ t('objects.wizard.seasons.forGuests', { n: occ }) }}</span>
                            @if (isAmountMode(unitType.id)) {
                              <p-inputnumber
                                [ngModel]="getOccupancyValue(unitType.id, occ)"
                                (ngModelChange)="setOccupancyValue(unitType.id, occ, $event)"
                                [min]="0"
                                mode="currency"
                                currency="PLN"
                                locale="pl-PL"
                                [fluid]="true"
                                [placeholder]="getFormUnitPrice(unitType.id) + ' zł'"
                              />
                            } @else {
                              <p-inputnumber
                                [ngModel]="getOccupancyValue(unitType.id, occ)"
                                (ngModelChange)="setOccupancyValue(unitType.id, occ, $event)"
                                [min]="0"
                                [max]="50"
                                prefix="-"
                                suffix="%"
                                [fluid]="true"
                                placeholder="0"
                              />
                            }
                          </div>
                        }
                      </div>
                      @if (occupancyPercentPreview(unitType.id); as preview) {
                        @if (preview.length > 0) {
                          <div class="occupancy-preview" data-testid="season-occupancy-preview">
                            <span class="occupancy-preview__title">{{ t('objects.wizard.seasons.occupancyPreviewTitle') }}</span>
                            <div class="occupancy-preview__rows">
                              @for (row of preview; track row.occupants) {
                                <div class="occupancy-preview__row">
                                  <span class="occupancy-preview__occ">{{ t('objects.wizard.seasons.forGuests', { n: row.occupants }) }}</span>
                                  <span class="occupancy-preview__amount">{{ row.amount }} zł</span>
                                  <span class="occupancy-preview__pct">−{{ row.percent }}%</span>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      }
                      <small class="hint-text">{{ t('objects.wizard.seasons.occupancyPricingHint') }}</small>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
          <p class="hint-text">{{ t('objects.wizard.seasons.priceRequiredHint') }}</p>
        </div>
      }

      <!-- Action Buttons -->
      <div class="form-actions">
        <p-button
          data-testid="season-inline-cancel-btn"
          [label]="t('common.cancel')"
          icon="pi pi-times"
          severity="secondary"
          [outlined]="true"
          (click)="isEditing() ? cancelEdit() : cancelAdd()"
        />
        <p-button
          data-testid="season-inline-save-btn"
          [label]="isEditing() ? t('objects.wizard.seasons.saveChanges') : t('objects.wizard.seasons.addSeason')"
          icon="pi pi-check"
          [disabled]="!isFormValid()"
          (click)="saveSeason()"
        />
      </div>
    </div>
  </ng-template>
</div>
